import {
  AmbientLight,
  CanvasTexture,
  ColorManagement,
  Group,
  LinearSRGBColorSpace,
  OrthographicCamera,
  Quaternion,
  Raycaster,
  Scene,
  Sprite,
  SpriteMaterial,
  WebGLRenderer,
} from 'three';
import type { Intersection, Vector2, Vector3 } from 'three';

import { noop } from '@frontend/utils';

import {
  AXES_DEFAULTS,
  COLOR_CODES,
  COLORS,
  CUBE_DEFAULTS,
} from '../constants';
import { getRelativeCoordinates } from '../utils';
import { createSpotLight } from './creation-utils';
import { makeCubeGeometry } from './cube-utils';
import type { CubeMesh } from './cube-utils';
import type { DecodedState } from './load-utils';
import type { MouseMarker, Renderer, RendererState } from './renderer';
import { snapToCubeNavigationView } from './snapto-utils';
import { makeMaterialOpacityTween } from './tween-utils';

//IMPORTANT: ColorManagement must be disabled before THREE.Color is used
//See https://discourse.threejs.org/t/updates-to-color-management-in-three-js-r152/50791
ColorManagement.enabled = false;

const makeAxisLabel = (label: string, color: string): Sprite => {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;
  const fontsize = 75;
  const fontface = 'Arial';
  context.font = fontsize + 'px ' + fontface;
  const metrics = context.measureText(label);
  const textWidth = metrics.width;
  context.fillStyle = color;
  context.fillText(
    label,
    (canvas.width - textWidth) / 2,
    canvas.height - (canvas.height - fontsize) / 2,
  );

  const texture = new CanvasTexture(canvas);
  texture.generateMipmaps = false;
  const spriteMaterial = new SpriteMaterial({
    map: texture,
  });

  // this makes it visible from everywhere
  spriteMaterial.depthTest = false;
  const sprite = new Sprite(spriteMaterial);
  sprite.scale.set(20, 20, 1);

  return sprite;
};

export class Cube {
  public readonly camera: OrthographicCamera;
  private readonly scene: Scene = new Scene();
  public readonly renderer: WebGLRenderer;
  private _isVisible = false;
  private selectables: CubeMesh[];
  private group: Group;
  private mouse: MouseMarker;
  private intersection: CubeMesh | null;

  constructor(public readonly parent: Renderer, private addToScene = true) {
    this.renderer = this.createRenderer(addToScene);
    this.camera = this.createCamera();
    createSpotLight(this);
    this.createLight();
    this.makeAxes();
    // selectables is the geomtries list used for calculating raycasting intersections
    // group is the geomtries used for display of the cube surfaces and hidden geometries for selectable surfaces
    const { group, selectables } = makeCubeGeometry(new Group(), []);
    group.name = 'cube';
    this.group = group;
    this.selectables = selectables;
    this.scene.name = 'cube';
    this.scene.add(this.group);
    this.mouse = { x: 0, y: 0 }; // onMouseMove marker
    this.intersection = null;

    this.setUpEventListeners();
  }

  public get isVisible(): boolean {
    //Note: using this._isVisible because it is faster than
    //returning `this.parent.node.contains(this.renderer.domElement)` which is important for this.render() performance
    //Performance is not a concern for this.show() and this.hide(). So using `this.parent.node.contains(this.renderer.domElement)` which is more accurate
    return this._isVisible;
  }

  public show(): void {
    const parentNode = this.parent.node;
    if (
      parentNode &&
      this.addToScene &&
      //Testing !this.parent.node.contains(this.renderer.domElement) rather than this._isVisible for accuracy (should be same as !this._isVisible)
      !parentNode.contains(this.renderer.domElement)
    ) {
      parentNode.appendChild(this.renderer.domElement);
    }
    this._isVisible = true; //Faster than using this.parent.node.contains(this.renderer.domElement)
  }

  public hide(): void {
    //Testing this.parent.node.contains(this.renderer.domElement) rather than this._isVisible for accuracy (should be same as this._isVisible)
    const parentNode = this.parent.node;
    if (parentNode && parentNode.contains(this.renderer.domElement)) {
      parentNode.removeChild(this.renderer.domElement);
    }
    this._isVisible = false;
  }

  public render(): void {
    if (this.isVisible) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  public get parentCamera(): OrthographicCamera {
    return this.parent.camera;
  }

  public update() {
    this.camera.position.subVectors(
      this.parent.camera.position,
      this.parent.trackball.target,
    );
    this.camera.position.setLength(AXES_DEFAULTS.CAM_DISTANCE);
    this.camera.lookAt(this.scene.position);
  }

  private createRenderer(addToScene: boolean): WebGLRenderer {
    const renderer = new WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance',
    });
    //See https://discourse.threejs.org/t/updates-to-color-management-in-three-js-r152/50791
    //and https://github.com/mrdoob/three.js/wiki/Migration-Guide#151--152
    //r152 introduced default ouputColorSpace of `sRGB`. This broke the look of colors. So setting back to ''
    renderer.outputColorSpace = LinearSRGBColorSpace;
    renderer.setPixelRatio(this.parent.nodeWindow.devicePixelRatio);
    renderer.setSize(AXES_DEFAULTS.AXES_SIZE, AXES_DEFAULTS.AXES_SIZE);
    renderer.setClearColor(COLOR_CODES.scene_color, 0);
    renderer.domElement.style.position = 'absolute';
    renderer.domElement.style.bottom = '0px';
    renderer.domElement.style.left = '0px';
    if (addToScene) {
      this.parent.node.appendChild(renderer.domElement);
    }
    return renderer;
  }

  private createCamera(): OrthographicCamera {
    const camera = new OrthographicCamera(
      AXES_DEFAULTS.AXES_SIZE / -12,
      AXES_DEFAULTS.AXES_SIZE / 12,
      AXES_DEFAULTS.AXES_SIZE / 12,
      AXES_DEFAULTS.AXES_SIZE / -12,
      0.1,
      2000,
    );
    camera.up = this.parent.camera.up;
    this.scene.add(camera);
    return camera;
  }

  private createLight(): void {
    const light = new AmbientLight(COLORS.soft_white, 0.8);
    this.scene.add(light);
  }

  private makeAxes(): void {
    const l = CUBE_DEFAULTS.CUBE_LENGTH;
    const originP = -l / 2;

    // const c = makeLabel('Y', COLOR_CODES.Y_s);
    // this.parent.node.appendChild(c);

    const xSprite = makeAxisLabel('X', COLOR_CODES.X_s);
    xSprite.position.set(
      originP * -1 + 2 + CUBE_DEFAULTS.CUBE_AXES_OVERHANG,
      originP,
      originP,
    );
    this.scene.add(xSprite);

    const ySprite = makeAxisLabel('Y', COLOR_CODES.Y_s);
    ySprite.position.set(
      originP,
      originP * -1 + 2 + CUBE_DEFAULTS.CUBE_AXES_OVERHANG,
      originP,
    );
    this.scene.add(ySprite);

    const zSprite = makeAxisLabel('Z', COLOR_CODES.Z_s);
    zSprite.position.set(
      originP,
      originP,
      originP * -1 + 2 + CUBE_DEFAULTS.CUBE_AXES_OVERHANG,
    );
    this.scene.add(zSprite);
  }

  public dispose() {
    try {
      this.parent.node.removeChild(this.renderer.domElement);
    } catch {
      //ok if the parent node doesn't have this.renderer.domElement
    }
    this.renderer.dispose();

    this.renderer.domElement.removeEventListener('mousemove', this.onMouseMove);
    this.renderer.domElement.removeEventListener('mousedown', this.onMouseDown);
    this.renderer.domElement.removeEventListener('click', this.onClick);
    this.onMouseMove = this.onMouseDown = this.onClick = noop;
  }

  private setUpEventListeners(): void {
    this.renderer.domElement.oncontextmenu = () => {
      return false;
    };
    this.renderer.domElement.addEventListener('mousemove', this.onMouseMove);
    this.renderer.domElement.addEventListener('mousedown', this.onMouseDown);
    this.renderer.domElement.addEventListener('click', this.onClick);
  }

  private onMouseMove = (e: MouseEvent): void => {
    const { x, y } = getRelativeCoordinates(e, this.renderer.domElement);
    const { width, height } = this.renderer.domElement;
    const devicePixelRatio = this.parent.nodeWindow.devicePixelRatio;
    //transform x, y into a range between -1 and 1
    this.mouse.x = ((x * devicePixelRatio) / width) * 2 - 1;
    this.mouse.y = ((y * devicePixelRatio) / height) * -2 + 1;

    // If no intersection, send event to renderer domElement
    if (!this.handleMousePosition(this.mouse)) {
      const newEvent = new MouseEvent(e.type, e);
      this.parent.renderer.domElement.dispatchEvent(newEvent);
    }
  };

  private onMouseDown = (e: MouseEvent): void => {
    e.preventDefault();

    // If no intersection, send event to renderer domElement
    if (!this.handleMousePosition(this.mouse)) {
      const newEvent = new MouseEvent(e.type, e);
      this.parent.renderer.domElement.dispatchEvent(newEvent);
    }
  };

  private onClick = (e: MouseEvent): void => {
    e.preventDefault();

    this.handleMousePosition(this.mouse);

    if (this.intersection) {
      // Wiki discusses below logic at: https://paperlessparts.atlassian.net/wiki/spaces/ENGINEERIN/pages/314277993/Viewer+Cube+Logic

      // Use a quaternion to track the rotation of the Position vector
      const quaternion = new Quaternion();
      quaternion.setFromUnitVectors(
        this.camera.position.clone().normalize(), // old cube camera Position
        this.intersection.userData.position.clone().normalize(), // new cube camera Position
      );

      // Apply the quaternion rotation to the old Up vector to rotate it into the same plane as 'candidate' vectors
      const localUp = this.camera.up.clone().normalize();
      localUp.applyQuaternion(quaternion);

      // Define the possible new Up vectors (in a plane perpendicular to the normal of the 'selected' face)
      // 1 of these 4 (or 6) 'candidate' vectors will be the new Up vector
      const upMap = new Map<number, Vector3>(
        this.intersection.userData.up.map((candidate) => {
          // Calculate angles between the local Up vector and candidate Up vectors
          const angle = localUp.angleTo(candidate);
          return [angle, candidate];
        }),
      );

      // New Up vector is candidate Up vector with smallest angle between it and local Up vector
      const newUp = upMap.get(Math.min(...upMap.keys())) as Vector3;

      const nextState: RendererState = { ...this.parent.state };
      nextState.cameraUp = newUp;

      // Set Renderer camera position based on current zoom and trackball offset
      // Takes the cube face normal as a unit vector
      // Multiplies it by the Zoom scalar
      // Offsets the result by the trackball target offset (to move from coord 0,0,0 to trackball lookat)
      const zoomScalar = this.parent.camera.position
        .clone()
        .sub(this.parent.trackball.target)
        .length();
      const newPosition = this.intersection.userData.position
        .clone()
        .multiplyScalar(zoomScalar)
        .add(this.parent.trackball.target);
      nextState.cameraPosition = newPosition;

      // Skip Tween if camera position + up vectors have not changed
      if (
        (!this.intersection.userData.up ||
          this.parent.camera.up.equals(nextState.cameraUp)) &&
        this.parent.camera.position.equals(nextState.cameraPosition)
      ) {
        return;
      }

      // Tween to new camera position
      snapToCubeNavigationView(
        nextState as unknown as DecodedState,
        this.parent,
        this.intersection.material,
      );
    }
  };

  // When cube faces are hovered or clicked, check for intersection. If matches, set hover value to face
  // Trigger repaint of cube with that face
  private handleMousePosition = (mousePosition: MouseMarker): boolean => {
    const newIntersection = this.performRaycasterObjectIntersect(mousePosition);

    this.handleIntersectionUpdate(newIntersection);

    return !!newIntersection;
  };

  private performRaycasterObjectIntersect = (
    mousePosition: MouseMarker,
  ): CubeMesh | null => {
    // Find intersections
    const raycaster = new Raycaster();
    (raycaster.params.Line as { threshold: number }).threshold = 2;
    (raycaster.params.Points as { threshold: number }).threshold = 2;
    raycaster.setFromCamera(mousePosition as Vector2, this.camera);
    const intersections: Intersection<CubeMesh>[] = raycaster.intersectObjects(
      this.selectables,
      false,
    );
    return intersections.length ? intersections[0].object : null;
  };

  public handleIntersectionUpdate = (newInt: CubeMesh | null) => {
    if (!this.intersection) {
      if (!newInt) {
        return;
      } else {
        this.handleMaterialUpdate(newInt, true);
        this.intersection = newInt;
      }
    } else {
      if (!newInt) {
        this.handleMaterialUpdate(this.intersection);
        this.intersection = null;
      } else if (this.intersection !== newInt) {
        this.handleMaterialUpdate(this.intersection);
        this.handleMaterialUpdate(newInt, true);
        this.intersection = newInt;
      } else {
        return;
      }
    }
    this.parent.requestRenderIfNotRequested();
  };

  private handleMaterialUpdate = (
    geom: CubeMesh,
    show = false,
  ): Promise<void> => {
    return makeMaterialOpacityTween(
      this.parent,
      geom.material,
      show ? CUBE_DEFAULTS.CUBE_OPACITY : 0,
    );
  };
}

export default Cube;
